import { AlertTriangle, Clock, Link as LinkIcon, Send } from "lucide-react"
import { useTranslation } from "react-i18next"

import { CandidateReviewActions } from "@/components/candidates/candidate-review-actions"
import { TiktokProfileEmbed } from "@/components/candidates/tiktok-profile-embed"
import { TiktokProfileFallback } from "@/components/candidates/tiktok-profile-fallback"
import { CardBrandBadge } from "@/components/home-triage/card-brand-badge"
import TiktokLinkStateCue from "@/components/tiktok-link-state-cue"
import { TiktokVerifiedBadge } from "@/components/tiktok-verified-badge"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import type { CandidateReviewItem } from "@/types/home-triage"

/**
 * OKS-706 — One Candidates-to-review card.
 *
 * Content hierarchy (UX Spec §2, top→bottom):
 *   1. `TiktokProfileEmbed` (reuse OKS-628, server-sanitized oEmbed) capped
 *      to card width — or the graceful fallback when `tiktok_embed_html`
 *      is null;
 *   2. identity row — `@handle` + `TiktokVerifiedBadge` + `TiktokLinkStateCue`;
 *   3. facts line — follower count where available;
 *   4. stale cue — muted "waiting N days" pill (icon + text, never colour
 *      alone) when past the server stale threshold;
 *   5. action row — contextual inline actions (`CandidateReviewActions`).
 *
 * `verified`/`link_state` are inert for manual-entry candidates (the
 * shared identity components render nothing for `false`/`null`), so the
 * card stays reusable by the OAuth-backed sibling consumers.
 */
export interface CandidateReviewCardProps {
  item: CandidateReviewItem
  className?: string
}

export function CandidateReviewCard({
  item,
  className,
}: CandidateReviewCardProps) {
  const { t } = useTranslation()

  return (
    <Card
      data-testid="candidate-review-card"
      data-candidate-id={item.id}
      className={cn(className)}
    >
      <CardContent className="flex flex-col gap-3 pt-6">
        {/* OKS-708 — brand badge (agency cross-brand queue only). */}
        {item.brand && <CardBrandBadge brand={item.brand} />}

        {item.tiktok_embed_html ? (
          <TiktokProfileEmbed
            html={item.tiktok_embed_html}
            handle={item.tiktok_handle}
          />
        ) : (
          <TiktokProfileFallback handle={item.tiktok_handle} />
        )}

        {/* Identity row. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">@{item.tiktok_handle}</span>
          <TiktokVerifiedBadge verified={item.verified} />
          <TiktokLinkStateCue state={item.link_state} />
        </div>

        {/* Facts line. */}
        <p className="text-muted-foreground text-sm">
          {item.followers === null
            ? t("candidates.home_review.facts.followers_unknown")
            : t("candidates.home_review.facts.followers", {
                count: item.followers,
                formatted: new Intl.NumberFormat().format(item.followers),
              })}
        </p>

        {/*
          OKS-950 — the agency's case for this creator, or after an appeal its
          argumentaire. Sits directly under the identity row, which is where a
          brand manager looks after "who is this".

          A NULL note renders nothing at all — no container, no em-dash (UX
          Spec §3 Edge). `entry_note` is structurally null on the
          candidates-to-review consumers, so this block is invisible there
          without either section needing to know about the other.

          Clamped to 3 lines (2 at 375px) so a long note can never push the
          action row off the card; the full text is on the detail page.
        */}
        {item.entry_note && (
          <p
            data-testid="candidate-review-entry-note"
            className="text-muted-foreground line-clamp-2 text-sm sm:line-clamp-3"
          >
            {item.entry_note}
          </p>
        )}

        {/*
          OKS-951 — the follow-up cue, « %{n} tentatives · dernière il y a %{d} j ».

          Emitted only by the « À relancer » section, so `attempt_count` is
          undefined on the three sibling consumers and this block is invisible
          there without either section knowing about the other — the same shape
          as `entry_note` above.

          🔴 `attempt_count` is TOTAL, never cycle-scoped, and there is
          deliberately NO zero branch: `mark_contacted` and the first attempt row
          are written in one transaction, and that service is the only writer of
          the event, so a `contacted` candidate always has at least one attempt.
          A zero branch here would paper over a broken invariant rather than
          render a real state.
        */}
        {item.attempt_count !== undefined && (
          <span
            data-testid="candidate-review-follow-up-cue"
            className="text-muted-foreground inline-flex items-center gap-1 text-sm"
          >
            <Send className="size-3.5 shrink-0" aria-hidden="true" />
            {t("candidates.home_follow_up.cue", {
              count: item.attempt_count,
              days: item.days_since_last_attempt ?? 0,
            })}
          </span>
        )}

        {/*
          OKS-953 — the segment-5 sub-state line.

          Emitted only by « Invitations en veille », so `invite` is undefined on
          the four sibling consumers and this block is invisible there without
          either section knowing about the other — the same shape as
          `entry_note` and `attempt_count` above.

          Sub-state, silence and expiry are TEXT, never colour (UX Spec §6):
          "opened but not finished" and "never opened" are two different
          problems with two different fixes, and a colour cannot say which.
        */}
        {item.invite && (
          <div
            data-testid="candidate-review-invite-quiet"
            className="flex flex-col gap-1 text-sm"
          >
            <span className="inline-flex items-center gap-1">
              <LinkIcon className="size-3.5 shrink-0" aria-hidden="true" />
              {t(
                item.invite.sub_state === "unclicked"
                  ? "candidates.home_invite_stale.state.unclicked"
                  : "candidates.home_invite_stale.state.clicked_incomplete",
              )}
            </span>
            <span className="text-muted-foreground">
              {t("candidates.home_invite_stale.cue", {
                count: item.invite.silent_days,
              })}
            </span>
            {/*
              Link-life cue. `expired` and the countdown are mutually exclusive
              by construction — the server nulls `expiring_soon_days` once the
              link is dead — so this never says both.
            */}
            {item.invite.link_expired ? (
              <span
                data-testid="candidate-review-invite-expired"
                className="text-destructive inline-flex items-center gap-1 font-medium"
              >
                <AlertTriangle
                  className="size-3.5 shrink-0"
                  aria-hidden="true"
                />
                {t("candidates.home_invite_stale.expiry.expired")}
              </span>
            ) : (
              item.invite.expiring_soon_days !== null && (
                <span
                  data-testid="candidate-review-invite-expiring"
                  className="text-muted-foreground"
                >
                  {t("candidates.home_invite_stale.expiry.soon", {
                    count: item.invite.expiring_soon_days,
                  })}
                </span>
              )
            )}
            {/*
              D2 — PERSISTENT card text, never a toast. It is a RULE, and a
              rule that expires after four seconds is not a rule (UX Spec §4 /
              OQ-E). It sits with the link, where the person about to copy it
              is looking.
            */}
            <span className="text-muted-foreground text-xs">
              {t("candidates.home_invite_stale.actions.reshare_hint")}
            </span>
          </div>
        )}

        {/*
          Wait cue — icon + text, never colour alone (UX Spec §6).

          OKS-950 splits this in two by SEVERITY, both server-owned: `stale`
          from day 3, `attention` from day 14. `attention` wins when both are
          set — it is the same fact, escalated, so rendering both would say it
          twice. Neither threshold is recomputed from `waiting_days` here: the
          server owns them, and a client-side copy drifts silently.
        */}
        {(item.stale || item.attention) && (
          <span
            data-testid={
              item.attention
                ? "candidate-review-attention"
                : "candidate-review-stale"
            }
            className={cn(
              "inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
              item.attention
                ? "bg-destructive/10 text-destructive"
                : "text-muted-foreground bg-muted",
            )}
          >
            {item.attention ? (
              <AlertTriangle className="size-3" aria-hidden="true" />
            ) : (
              <Clock className="size-3" aria-hidden="true" />
            )}
            {t(
              item.attention
                ? "candidates.home_review.attention"
                : "candidates.home_review.stale",
              { count: item.waiting_days },
            )}
          </span>
        )}

        <CandidateReviewActions
          actions={item.actions}
          handle={item.tiktok_handle}
          // OKS-953 — segment 5's copyable link arrives on the `invite`
          // sub-object; the four sibling sections use `invite_url`. Feeding
          // both into the SHIPPED `InviteCopyButton` reuses its clipboard +
          // toast wiring rather than forking a second copy affordance.
          inviteUrl={item.invite?.url ?? item.invite_url}
          escalatedAt={item.escalated_at}
          // OKS-952 — the from_status a refusal is recorded against. Set here,
          // once: this card is the single hop between all FIVE consuming
          // sections and the action row, so every one of them (brand home
          // review, brand-validation queue, awaiting-brand, follow-up, and
          // AGENCY home) gets the right taxonomy slice from one edit.
          status={item.status}
          className="mt-1"
        />
      </CardContent>
    </Card>
  )
}
